import DlsLink from "@galaxy-io/dls/links/Link";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import type { ActivityItem } from "@gtm/contracts";
import { sourceName } from "@gtm/contracts";
import { styled } from "@linaria/react";
import { LinkIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { QueryState } from "@/components/query-state";
import {
  DrawerExternalLink,
  DrawerList,
  DrawerRow,
  DrawerSection,
} from "@/components/workspace/drawer-content";
import { RelativeTime } from "@/components/workspace/relative-time";
import { SourceIcon } from "@/components/workspace/source-icon";
import { CompanyLink } from "@/features/companies/company-link";
import { PersonLink } from "@/features/people/person-link";
import { Avatar } from "@/features/people/profile-drawer";
import { apiClient, unwrap } from "@/lib/api-client";
import { safeUrl } from "@/lib/utils";

const Profile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[16]};
  min-width: 0;
`;
// The name is whatever the source gave us — titles and postnominals included —
// so the header never wraps: the avatar holds its size and the name truncates.
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[8]};
  min-width: 0;
  > :first-child {
    flex-shrink: 0;
  }
`;
const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[4]};
  flex: 1;
  min-width: 0;
`;
// Several sources can resolve to one person, so the handles wrap onto their own
// rows rather than the header claiming a single origin.
const Sources = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${t.space[2]} ${t.space[8]};
  min-width: 0;
`;
const Source = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  min-width: 0;
`;

export function SwipePerson({ item }: { item: ActivityItem }) {
  const result = useQuery({
    queryKey: ["swipe-person", item.person_id],
    enabled: !!item.person_id,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/people/{id}", {
          params: { path: { id: item.person_id! } },
          signal,
        }),
      ),
    staleTime: 30_000,
  });
  const person = result.data;
  // Lead with the source this signal came from, then every other source the
  // person is known by. Before the profile loads, the signal's own identity
  // stands in, so the row fills out rather than appearing.
  const known = person?.identities.length
    ? person.identities
    : item.login
      ? [{ provider: item.provider, handle: item.login }]
      : [];
  const identities = [
    ...known.filter((identity) => identity.provider === item.provider),
    ...known.filter((identity) => identity.provider !== item.provider),
  ];
  return (
    <Profile>
      <Header>
        <Avatar src={item.avatar || ""} name={item.name || item.login || "Unknown"} />
        <Identity>
          {item.person_id ? (
            <PersonLink id={item.person_id}>
              <Text size={TextSize.BODY_LG} weight={TextWeight.MEDIUM} lineClamp={1}>
                {item.name || item.login}
              </Text>
            </PersonLink>
          ) : (
            <Text weight={TextWeight.MEDIUM} lineClamp={1}>
              {item.name || item.login || "Unknown account"}
            </Text>
          )}
          {!!identities.length && (
            <Sources>
              {identities.map((identity) => (
                <Source key={`${identity.provider}:${identity.handle}`}>
                  <SourceIcon source={identity.provider} size={14} />
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                    {identity.handle ? `@${identity.handle}` : sourceName(identity.provider)}
                  </Text>
                </Source>
              ))}
            </Sources>
          )}
        </Identity>
      </Header>
      {item.person_id && (result.isPending || result.isError) && (
        <QueryState
          error={result.error}
          loadingMessage="Loading profile…"
          onRetry={() => void result.refetch()}
        />
      )}
      {person && (
        <>
          {person.bio && <Text size={TextSize.BODY_SM}>{person.bio}</Text>}
          <DrawerList>
            {person.company && (
              <DrawerRow label="Company">
                {person.companyId ? (
                  <CompanyLink id={person.companyId}>{person.company}</CompanyLink>
                ) : (
                  person.company
                )}
              </DrawerRow>
            )}
            {person.location && <DrawerRow label="Location">{person.location}</DrawerRow>}
            <DescriptionList
              items={[
                {
                  label: "First seen",
                  value: (
                    <>
                      <Text size={TextSize.BODY_SM}>
                        <RelativeTime value={person.firstSeenAt} />
                      </Text>
                    </>
                  ),
                },
                { label: "Signals", value: person.activity.total },
              ]}
            />
          </DrawerList>
          <DrawerSection
            variant={WidgetVariant.TERTIARY}
            header="Social"
            icon={LinkIcon}
            defaultIsOpen
          >
            {person.identities
              .filter((identity) => safeUrl(identity.url))
              .map((identity) => (
                <DrawerRow
                  key={`${identity.provider}:${identity.url}`}
                  label={sourceName(identity.provider)}
                >
                  <DlsLink href={safeUrl(identity.url)!} isExternal>
                    <Text as="span" size={TextSize.BODY_SM}>
                      {identity.url}
                    </Text>
                  </DlsLink>
                </DrawerRow>
              ))}
            {person.publicEmail && (
              <DrawerRow label="Public email">
                <DlsLink href={`mailto:${encodeURIComponent(person.publicEmail)}`}>
                  <Text as="span" size={TextSize.BODY_SM}>
                    {person.publicEmail}
                  </Text>
                </DlsLink>
              </DrawerRow>
            )}
            {safeUrl(person.website) && (
              <DrawerRow label="Website">
                <DrawerExternalLink
                  href={safeUrl(person.website)!}
                  target="_blank"
                  rel="noreferrer"
                  title={person.website || undefined}
                >
                  <Text as="span" size={TextSize.BODY_SM}>
                    {person.website}
                  </Text>
                </DrawerExternalLink>
              </DrawerRow>
            )}
            {person.twitterUsername && (
              <DrawerRow label="X">
                <DlsLink
                  href={`https://x.com/${encodeURIComponent(person.twitterUsername)}`}
                  isExternal
                >
                  <Text as="span" size={TextSize.BODY_SM}>
                    https://x.com/{person.twitterUsername}
                  </Text>
                </DlsLink>
              </DrawerRow>
            )}
            {person.socialAccounts
              .filter(
                (account) =>
                  safeUrl(account.url) &&
                  !(
                    person.twitterUsername &&
                    ["twitter", "x"].includes(account.provider.toLowerCase())
                  ),
              )
              .map((account) => (
                <DrawerRow
                  key={account.url}
                  label={
                    ["twitter", "x"].includes(account.provider.toLowerCase())
                      ? "X"
                      : account.provider.toLowerCase() === "linkedin"
                        ? "LinkedIn"
                        : account.provider.charAt(0).toUpperCase() + account.provider.slice(1)
                  }
                >
                  <DlsLink href={safeUrl(account.url)!} isExternal>
                    <Text as="span" size={TextSize.BODY_SM}>
                      {account.url}
                    </Text>
                  </DlsLink>
                </DrawerRow>
              ))}
          </DrawerSection>
        </>
      )}
    </Profile>
  );
}
